import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { NAV_ENTRIES, entryForRoute } from "../navigation";

/**
 * Breadcrumb for a sub-page — a reachable page the rail does not list on its
 * own, so it highlights the parent instead (`subPageOf`). Without a breadcrumb
 * the user can be on a page the rail never names; this shows "Parent / This
 * page", with the parent as a link back.
 *
 * Renders nothing on a top-level page: the rail already names where you are.
 */
const NavBreadcrumb = () => {
  const { pathname } = useLocation();
  const entry = entryForRoute(pathname);
  if (!entry) return null;
  // A top-level rail destination needs no breadcrumb — the rail already names
  // it. Only pages reached from elsewhere (hidden, or a declared sub-page) get
  // one.
  if (!entry.hidden && !entry.subPageOf) return null;

  const parent = entry.subPageOf
    ? NAV_ENTRIES.find((candidate) => candidate.id === entry.subPageOf)
    : null;

  // Home → [sub-page parent, if any] → current. Rooting at Home gives a way
  // back for a page reached from the landing surface, where a stale `subPageOf`
  // parent would point somewhere the page no longer lives under.
  const trail = [
    { key: "home", label: "Home", to: "/" },
    ...(parent ? [{ key: parent.id, label: parent.label, to: parent.route }] : []),
    { key: entry.id, label: entry.label, current: true },
  ];

  return (
    <nav
      aria-label="Breadcrumb"
      className="mb-4 flex items-center gap-1.5 text-sm text-[#00000073]"
    >
      {trail.map((crumb, index) => (
        <span
          key={crumb.key}
          className="flex items-center gap-1.5"
        >
          {index > 0 && (
            <ChevronRight
              className="h-4 w-4 shrink-0"
              aria-hidden="true"
            />
          )}
          {crumb.current ? (
            <span
              aria-current="page"
              className="text-[#000000d9]"
            >
              {crumb.label}
            </span>
          ) : (
            <Link
              to={crumb.to}
              className="hover:text-[#0e49b5]"
            >
              {crumb.label}
            </Link>
          )}
        </span>
      ))}
    </nav>
  );
};

export default NavBreadcrumb;
